import { ColorArea, IconButton, HueSlider, OpacitySlider } from '@figma/fpl-components';
import { Icon24Eyedropper } from '@figma/fpl-icons';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import * as colorPicker from '@zag-js/color-picker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { Dropdown, type Option } from '../Dropdown/Dropdown';
import { ColorInput } from './ColorInput';
import { resolveSliderCommit } from './resolveSliderCommit';
import { useControlledColorDraft } from './useControlledColorDraft';
import { useEyeDropper } from './useEyeDropper';
import { usePendingColorChange, type PendingColorSource } from './usePendingColorChange';
import type { ColorFormat, ColorPickerProps } from './ColorPicker';

const formatOptions: Option<ColorFormat>[] = [
  { id: 'hex', label: 'HEX', value: 'HEX' },
  { id: 'rgb', label: 'RGB', value: 'RGB' },
  { id: 'hsl', label: 'HSL', value: 'HSL' },
];

const styles = stylex.create({
  root: {
    display: 'flex',
    flexDirection: 'column',
    isolation: 'isolate',
    boxSizing: 'border-box',
    paddingTop: spacer.spacer16px,
    paddingRight: spacer.spacer16px,
    paddingBottom: `calc(${spacer.spacer16px} + ${spacer.spacer1})`,
    paddingLeft: spacer.spacer16px,
    width: 240,
  },
  area: {
    height: 208,
    width: '100%',
  },
  controls: {
    alignItems: 'center',
    display: 'flex',
    gap: spacer.spacer16px,
    marginTop: spacer.spacer12px,
    width: '100%',
  },
  sliders: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: spacer.spacer12px,
    minWidth: 0,
  },
  inputs: {
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: '64px minmax(0, 1fr)',
    gap: spacer.spacer2,
    marginTop: spacer.spacer16px,
    width: '100%',
  },
});

/** FPL composition with a synchronous draft for throttled controlled consumers. */
export function FplColorPicker({
  color,
  onChange,
  onChangeEnd,
  format: propFormat,
  onFormatChange: propOnFormatChange,
  showAlpha = true,
  onClick,
  onEyedropperActiveChange,
}: ColorPickerProps) {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ColorFormat>(propFormat || 'HEX');
  const pending = usePendingColorChange((pendingColor) => onChangeEnd(pendingColor.toFormat('rgba')));
  const { draft, getCurrent, update } = useControlledColorDraft(color, pending.hasPending);
  const eyedropper = useEyeDropper({
    onActiveChange: onEyedropperActiveChange,
    onPick: (hexColor) => emitFinal(colorPicker.parse(hexColor)),
  });

  function emitOngoing(nextColor: colorPicker.Color, source: PendingColorSource) {
    const hsba = update(nextColor);
    pending.begin(hsba, source);
    onChange?.(hsba.toFormat('rgba'));
  }

  function emitFinal(nextColor: colorPicker.Color) {
    const hsba = update(nextColor);
    pending.clear();
    onChangeEnd(hsba.toFormat('rgba'));
  }

  function handleFplSliderChange(
    nextColor: colorPicker.Color,
    commit: boolean,
    event: unknown,
    source: Exclude<PendingColorSource, 'area'>,
  ) {
    if (!commit) {
      emitOngoing(nextColor, source);
      return;
    }

    // The 1D FPL slider commits its controlled render value on pointer-up.
    // A throttled parent may not have echoed the last ongoing frame yet, so
    // commit our synchronous draft instead. FPL supplies the gesture's
    // starting value for cancellation, which must take precedence.
    emitFinal(resolveSliderCommit(event, nextColor, pending.latest()));
  }

  function emitInputFinal(nextColor: colorPicker.Color) {
    update(nextColor);
    pending.clear();
    // ColorInput is part of the legacy public API: HSL edits commit HSLA,
    // RGB edits RGBA, and HEX edits RGBA. Preserve that runtime format.
    onChangeEnd(nextColor);
  }

  function emitInputOngoing(nextColor: colorPicker.Color) {
    emitOngoing(nextColor, 'opacity');
  }

  const hue = draft.getChannelValue('hue') / 360;
  const saturation = draft.getChannelValue('saturation') / 100;
  const brightness = draft.getChannelValue('brightness') / 100;
  const alpha = draft.getChannelValue('alpha');
  const rgba = draft.toFormat('rgba');
  const opacityColor = {
    r: rgba.getChannelValue('red') / 255,
    g: rgba.getChannelValue('green') / 255,
    b: rgba.getChannelValue('blue') / 255,
  };
  const selectedFormat = propFormat ?? format;

  function onFormatChange(nextFormat: Option<ColorFormat>) {
    setFormat(nextFormat.value);
    propOnFormatChange?.(nextFormat.value);
  }

  return (
    <div data-testid="fpl-color-picker" ref={pending.rootRef} {...stylex.props(styles.root)} onClick={onClick}>
      <div {...stylex.props(styles.area)}>
        <ColorArea.HSB
          aria-label={t(I18N_KEYS.COMMON_COMPONENTS.COLOR_PICKER.COLOR_AREA_ARIA)}
          hue={hue}
          saturation={saturation}
          brightness={brightness}
          onChange={({ saturation: nextSaturation, brightness: nextBrightness }, { commit }) => {
            const nextColor = getCurrent()
              .withChannelValue('saturation', nextSaturation * 100)
              .withChannelValue('brightness', nextBrightness * 100);
            if (commit) emitFinal(nextColor);
            else emitOngoing(nextColor, 'area');
          }}
        />
      </div>

      <div {...stylex.props(styles.controls)}>
        <IconButton
          aria-label={t(I18N_KEYS.COMMON_COMPONENTS.COLOR_PICKER.EYEDROPPER_ARIA)}
          disabled={!eyedropper.supported}
          onClick={() => void eyedropper.pickColor()}
          size="md"
        >
          <Icon24Eyedropper />
        </IconButton>
        <div {...stylex.props(styles.sliders)}>
          <HueSlider
            aria-label={t(I18N_KEYS.COMMON_COMPONENTS.COLOR_PICKER.HUE_ARIA)}
            value={hue}
            onChange={(nextHue, { commit, event }) => {
              handleFplSliderChange(getCurrent().withChannelValue('hue', nextHue * 360), commit, event, 'hue');
            }}
            size="md"
          />
          {showAlpha && (
            <OpacitySlider
              ref={pending.opacityRef}
              aria-label={t(I18N_KEYS.COMMON_COMPONENTS.COLOR_PICKER.OPACITY_ARIA)}
              color={opacityColor}
              value={alpha}
              onChange={(nextAlpha, { commit, event }) => {
                handleFplSliderChange(getCurrent().withChannelValue('alpha', nextAlpha), commit, event, 'opacity');
              }}
              size="md"
            />
          )}
        </div>
      </div>

      <div {...stylex.props(styles.inputs)}>
        <Dropdown
          width="64px"
          size="small"
          value={selectedFormat}
          placeholder={selectedFormat}
          options={formatOptions}
          onChange={onFormatChange}
        />
        <ColorInput
          color={rgba}
          onChange={emitInputFinal}
          onChangeOngoing={emitInputOngoing}
          format={selectedFormat}
          showAlpha={showAlpha}
          fullWidth
          grouped
        />
      </div>
    </div>
  );
}
